export interface Author {
  id: string
  image: string
  defaultImage: string
  userName: string
  type: string
}

export interface MainCategory {
  code: string
}

export interface Price {
  price: number
  formattedPrice: string
}

export interface Article {
  code: string
  name: string
  brandName: string
  articleUrl: string
  productImage?: Image
  quickBuyLink: string
  images: ArticleImage
  mainCategory: MainCategory
  whitePrice: Price
  redPrice: Price
  yellowPrice: Price
  priorPrice?: Price
  new: boolean
  onSale: boolean
  hasPriceMarker: boolean
  inStock: boolean
  displayable: boolean
  isAddedToBoard?: boolean
}

export enum ImageType {
  DESCRIPTIVEDETAIL = 'DESCRIPTIVEDETAIL',
  ENVIRONMENT = 'ENVIRONMENT',
  DESCRIPTIVESTILLLIFE = 'DESCRIPTIVESTILLLIFE',
  LOOKBOOK = 'LOOKBOOK',
  STYLEBOARD_PROFILE = 'STYLEBOARD_PROFILE',
  STYLEBOARD_UPLOADED = 'STYLEBOARD_UPLOADED',
  STYLEBOARD_TEASER = 'STYLEBOARD_TEASER',
  STYLEBOARD_UGC = 'STYLEBOARD_UGC',
  STYLEBOARD_BG_IMAGE = 'STYLEBOARD_BG_IMAGE',
}
export interface Image {
  name?: string
  type: ImageType
  url: string
  caption?: string
}

export interface ArticleImage {
  selected: Image[]
  alternate: Image[]
}

export interface Item {
  id: string
  type: ItemType
  article?: Article
  images?: Image[]
  position?: string
}

export interface StyleboardLinkhref {
  href: string
}

export interface MyStyleboards {
  styleboards: Styleboard[]
  _links: {
    next: {
      href: string
    }
  }
}

export interface MyStyleboardsReponse extends MyStyleboards {
  mergeDeep: boolean
}

export interface StyleboardLink {
  next: StyleboardLinkhref
}
export interface Styleboard {
  id: string
  title: string
  author: Author
  image: string
  status: StyleboardStatus
  followers: number
  updated: boolean
  items: Item[]
  about: string
  type: StyleboardType
  totalNoItems?: number
  _links?: StyleboardLink
  similarItems?: SimilarItems
}

export enum StyleboardStatus {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

export enum StyleboardType {
  OWN = 'OWN',
  FOLLOWING = 'FOLLOWING',
  NOTFOLLOWING = 'NOTFOLLOWING',
}

export enum ItemType {
  ARTICLE = 'ARTICLE',
  IMAGE = 'IMAGE',
  BLOB_IMAGE = 'BLOB_IMAGE',
}

export interface StyleboardReadMoreProps {
  description: string
}

export interface StyleboardProductItemProps {
  item: Item
}

export enum StyleboardModalType {
  STYLEBOARD_DELETE_MODAL = 'STYLEBOARD_DELETE_MODAL',
  STYLEBOARD_EDIT_MODAL = 'STYLEBOARD_EDIT_MODAL',
  STYLEBOARD_EDIT_PROFILE_MODAL = 'STYLEBOARD_EDIT_PROFILE_MODAL',
  STYLEBOARD_OPTION_MODAL = 'STYLEBOARD_OPTION_MODAL',
  STYLEBOARD_ADD_MODAL = 'STYLEBOARD_ADD_MODAL',
  STYLEBOARD_ADD_PHOTO_MODAL = 'STYLEBOARD_ADD_PHOTO_MODAL',
  STYLEBOARD_EXPLORE_STYLES = 'STYLEBOARD_EXPLORE_STYLES',
  STYLEBOARD_UNFOLLOW_MODAL = 'STYLEBOARD_UNFOLLOW_MODAL',
  STYLEBOARD_INDIVIDUAL_IMAGE_MODAL = 'STYLEBOARD_INDIVIDUAL_IMAGE_MODAL',
  STYLEBOARD_MORE_OPTIONS_ITEM_MODAL = 'STYLEBOARD_MORE_OPTIONS_ITEM_MODAL',
}

export interface SocialMedia {
  media: string
  url: string
}

export interface Notification {
  id: string
  type: 'success' | 'info' | 'warning' | 'error'
  message: string
  notification: NotificationType
}

export enum NotificationType {
  COPY_STYLEBOARD_LINK = 0,
  STYELBOARD_DELETED_FAILED = 1,
  FOLLOW_STYLEBOARD_FAILED = 2,
  PUT_PROFILE_SUCCEEDED = 3,
  PUT_PROFILE_FAILED = 4,
  PUT_STYLEBOARD_FAILED = 5,
  PUT_STYLEBOARD_SUCCEDED = 6,
  ADD_IMAGE_STYLEBOARD_FAILED = 7,
  STYLEBOARD_ITEM_DELETE_FAILED = 8,
  FETCH_STYLEBOARDS_FAILED = 9,
  FETCH_FOLLOW_STYLEBOARDS_FAILED = 10,
  CHANGE_BACKGROUND_IMAGE_FAILED = 11,
}

export interface StyleboardNotificationProps {
  includeNotifications?: NotificationType[]
  width?: 'fitToTabContainer' | 'fullWidth'
}

export interface StyleboardSuccessLocalState {
  title: string
  about: string
  status: StyleboardStatus
}

export interface FetchStyleboardResponse {
  styleboard?: Styleboard
  items?: Item[]
  link?: string
}

export interface StyleboardHeroSectionChildProps {
  isLoggedIn: boolean
  isUserOwnerOfStyleboard: boolean
  user?: Author
  following: boolean
  isFollowingStyleboardLoading: boolean
  userFollowingCopyKey: string
  image?: string
  title?: string
  author?: Author
  items?: number
  followers?: number
  hrefToProfile?: string
  about?: string
  itemsLabel: string
  handleOnClickFollowButton: () => void
  handleOnClickProfileImage: () => void
}

export type ArticleFilter = { type: ItemType; article?: Article }

export interface PopularStyleboardsProps {
  styleboards: Styleboard[]
  onQuickFollow: (id: string) => void
}

export interface StyleboardTabToggleProps {
  onModalOpenToggle: (open: boolean, modalType: StyleboardModalType) => void
}

export interface SimilarItems {
  articles: Article[]
}

export interface FetchSimilarItemsResponse extends SimilarItems {
  predictionId: string
}

export interface StyleboardProductListingRowProps {
  rowItems: Item[]
  seenItemIds: string[]
  setSeenItemIds: any
}

export interface StyleboardSimilarItemListingRowProps {
  rowItems: ArticleWithPosition[]
  seenItemIds: string[]
  setSeenItemIds: React.Dispatch<React.SetStateAction<string[]>>
  onAddItemToStyleboard: (item: Article) => void
  predictionId: string
}

export interface SimilarItemProps {
  item: ArticleWithPosition
  onAddItemToStyleboard: (item: Article) => void
  predictionId: string
}

export interface PutItemToStyleboardRequest {
  styleboardIds: string[]
  items: {
    type: ItemType.ARTICLE
    article: {
      code: string
      name: string
    }
    images: {
      type?: string
      url: string
    }
  }[]
}

export enum UploadFileMode {
  PROFILE = 'PROFILE',
  PHOTO = 'PHOTO',
  BACKGROUND = 'BACKGROUND',
}
export interface StyleboardUploadFileProps {
  onFileChangeCallback: (files: FileList) => void
  mode: UploadFileMode
}
export interface StyleboardState {
  styleboard?: Styleboard
  myStyleboards?: MyStyleboards
  followStyleboards?: MyStyleboards
  popularStyleboards?: Styleboard[]
  notifications?: Notification[]
  isStyleboardLoading: boolean
  hasSimilarItemsLoaded: boolean
  hasStyleboardLoadingFailed: boolean
  isSimilarItemsLoading: boolean
  hasSimilarItemsLoadingFailed: boolean
  hasAddItemToboardSucceded: boolean
  hasAddItemToboardFailed: boolean
  isStyleboardsLoading: boolean
  hasStyleboardsLoadingFailed: boolean
  hasFollowStyleboardsLoaded: boolean
  hasFollowStyleboardsLoadedFailed: boolean
  isFollowingStyleboardLoading: boolean
  hasFollowingStyleboardLoadingFailed: boolean
  isUploadingImageLoading: boolean
  hasUploadingImageSucceded: boolean
  hasUploadingImageFailed: boolean
  individualItemId: string
  isDeletionItemLoading: boolean
  hasDeletedItemSucceded: boolean
  hasDeletedItemFailed: boolean
  isDeletingStyleboardLoading: boolean
  hasDeletedStyleboardSucceded: boolean
  hasDeletedStyleboardFailed: boolean
  hasPopularStyleboardsLoaded: boolean
  hasPopularStyleboardsLoadedFailed: boolean
  publicStyleboards?: MyStyleboards
  isPublicStyleboardsLoading: boolean
  hasPublicStyleboardsLoadingFailed: boolean
}

export type TrackViewStyleboardDetailPage = (
  styleboardTitle: string,
  styleboardId: string,
  styleboardType: StyleboardType | null,
  styleboardStatus: StyleboardStatus,
  styleboardFollowerCount: number,
  styleboardItemCount: number,
) => void

export type TrackClickOnArticleItem = (
  productName: [string],
  productId: [string],
  productPosition: [string],
  productBrand: [string],
  productCategory: [string],
) => void

export type TrackRowItemsImpression = (items: Item[]) => void

export type TrackViewStyleboardSimilarDetailPage = (
  styleboardTitle: string,
  styleboardId: string,
  styleboardType: StyleboardType | null,
  styleboardStatus: StyleboardStatus,
  styleboardFollowerCount: number,
  styleboardItemCount: number,
  similarItemsPredictionId: string,
) => void

export type TrackClickOnSimilarArticleItem = (
  productName: [string],
  productId: [string],
  productPosition: [string],
  productBrand: [string],
  productCategory: [string],
  similarItemsPredictionId: string,
) => void

export type TrackClickOnSimilarArticleItemPlusIcon = (
  styleboardTitle: string,
  similarItemsPredictionId: string,
) => void

export type TrackClickOnStyleboardTab = (styleboardTitle: string) => void
export interface ArticleWithPosition extends Article {
  position: string
}

export type TrackRowSimilarItemsImpression = (items: ArticleWithPosition[]) => void

export type TrackOSAEvent = (articleCode: string) => void

export interface StyleboardModalContextProps {
  isModalOpen: boolean
  modalType?: StyleboardModalType
  onModalOpenToggle: (isOpen: boolean, styleboardModalType: StyleboardModalType) => void
}

export enum PublicStyleboardSort {
  FOLLOWERS = 'followers',
  LAST_UPDATED = 'lastUpdated',
  CREATED = 'created',
}
